import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type {
  FeatureLimitCheckResponse,
  NumericFeatureKey,
  SubscriptionTier,
} from "../types/subscription.types";
import { FEATURE_LABELS } from "../types/subscription.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import {
  checkFeatureLimitQueryKey,
  useCheckFeatureLimitQuery,
} from "./useCheckFeatureLimitQuery";
import { subscriptionUsageQueryKey } from "./useSubscriptionUsageQuery";

interface UseFeatureLimitOptions {
  /** The feature to check the limit for */
  feature: NumericFeatureKey;
  /** Override the terreiro ID (defaults to cached terreiro) */
  terreiroId?: string;
  /** Override the medium ID (defaults to cached medium) - for personal subscriptions */
  mediumId?: string;
  /** Current count if already known */
  currentCount?: number;
  /** Enable/disable the query */
  enabled?: boolean;
}

interface UseFeatureLimitResult {
  /** Whether the user can create more of this resource */
  canCreate: boolean;
  /** Whether the limit check is still loading */
  isLoading: boolean;
  /** Whether there was an error checking the limit */
  isError: boolean;
  /** The full limit check response */
  limitInfo: FeatureLimitCheckResponse | null;
  /** Current usage count */
  currentCount: number;
  /** Maximum allowed (-1 = unlimited) */
  limit: number;
  /** Remaining allowed creations */
  remaining: number;
  /** Usage percentage (0-100) */
  percentage: number;
  /** True when usage >= 80% */
  isNearingLimit: boolean;
  /** True when limit is reached */
  isAtLimit: boolean;
  /** Suggested tier to upgrade to */
  suggestedUpgrade: SubscriptionTier | null;
  /** Human-readable message from API */
  message: string;
  /** Human-readable feature label (Portuguese) */
  featureLabel: string;
  /** Whether the limit is unlimited (-1) */
  isUnlimited: boolean;
  /** Invalidate the limit check cache (call after create/delete) */
  invalidate: () => Promise<void>;
  /** Refetch the limit check */
  refetch: () => void;
}

/**
 * Convenience hook that combines limit check with UI-friendly state
 *
 * Use this hook in create pages/forms to:
 * - Disable create buttons when limit is reached
 * - Show warnings when approaching the limit
 * - Display upgrade prompts
 * - Invalidate cache after resource creation
 *
 * @example
 * ```tsx
 * function CreateEventButton() {
 *   const {
 *     canCreate,
 *     isLoading,
 *     isNearingLimit,
 *     isAtLimit,
 *     message,
 *     featureLabel,
 *     invalidate,
 *   } = useFeatureLimit({ feature: 'maxEvents' });
 *
 *   const handleCreate = async () => {
 *     await createEvent();
 *     await invalidate(); // Refresh limit after creation
 *   };
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <>
 *       {isNearingLimit && <Warning>{message}</Warning>}
 *       {isAtLimit && <UpgradePrompt feature="maxEvents" />}
 *       <Button onClick={handleCreate} disabled={!canCreate}>
 *         Criar {featureLabel}
 *       </Button>
 *     </>
 *   );
 * }
 * ```
 */
export function useFeatureLimit({
  feature,
  terreiroId: overrideTerreiroId,
  mediumId: overrideMediumId,
  currentCount,
  enabled = true,
}: UseFeatureLimitOptions): UseFeatureLimitResult {
  const queryClient = useQueryClient();
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  const terreiroId = overrideTerreiroId ?? cachedTerreiro?.id;
  const mediumId = overrideMediumId ?? cachedMedium?.id;

  // Determine active context - memoized to prevent unnecessary re-renders
  const context = useMemo(() => {
    const hasTerreiro = !!terreiroId;
    const hasMedium = !hasTerreiro && !!mediumId;

    return hasTerreiro ? { terreiroId } : hasMedium ? { mediumId } : undefined;
  }, [terreiroId, mediumId]);

  const {
    data: limitInfo,
    isLoading,
    isError,
    refetch,
  } = useCheckFeatureLimitQuery({
    feature,
    terreiroId,
    mediumId,
    currentCount,
    enabled,
  });

  // Invalidate both limit check and usage queries for the active context
  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: checkFeatureLimitQueryKey(feature, context),
      }),
      queryClient.invalidateQueries({
        queryKey: subscriptionUsageQueryKey(context),
      }),
    ]);
  }, [queryClient, feature, context]);

  // Memoized computed values
  const result = useMemo<UseFeatureLimitResult>(() => {
    const defaults: UseFeatureLimitResult = {
      canCreate: true, // Default to allowing creation while loading
      isLoading,
      isError,
      limitInfo: limitInfo ?? null,
      currentCount: limitInfo?.currentCount ?? 0,
      limit: limitInfo?.limit ?? -1,
      remaining: limitInfo?.remaining ?? -1,
      percentage: limitInfo?.percentage ?? 0,
      isNearingLimit: limitInfo?.nearingLimit ?? false,
      isAtLimit: limitInfo?.upgradeRequired ?? false,
      suggestedUpgrade: limitInfo?.suggestedUpgrade ?? null,
      message: limitInfo?.message ?? "",
      featureLabel: FEATURE_LABELS[feature] ?? feature,
      isUnlimited: limitInfo?.limit === -1,
      invalidate,
      refetch,
    };

    if (!limitInfo) {
      return defaults;
    }

    return {
      ...defaults,
      canCreate: limitInfo.allowed,
    };
  }, [limitInfo, isLoading, isError, feature, invalidate, refetch]);

  return result;
}

/**
 * Hook to check multiple feature limits at once
 *
 * @example
 * ```tsx
 * const limits = useMultipleFeatureLimits(['maxEvents', 'maxActivities']);
 * const anyAtLimit = limits.some(l => l.isAtLimit);
 * ```
 */
export function useMultipleFeatureLimits(
  features: NumericFeatureKey[],
  terreiroId?: string,
): UseFeatureLimitResult[] {
  // This creates multiple hook calls, which is fine since features array
  // should be static. For dynamic features, use useSubscriptionUsageQuery instead.
  return features.map((feature) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useFeatureLimit({ feature, terreiroId }),
  );
}
